'use client'

import Breadcrumbs from '@/_components/Breadcrumbs'
import { ORDER_TYPE_IDS } from '@/_constants'
import { resolveBrandId } from '@/_lib/brand-href'
import { orderServiceMika } from '@/_service/orders.service'
import { IRedux } from '@/_store/types'

import { Box, Container, Flex, HStack, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useQueryState } from 'nuqs'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Header from '../../../_components/Header'
import BrandFilter from '../brand-filter'
import DeliveryTypes from '../delivery-types'
import LoaderSkeleton from './loader-skeleton'
import OrderList from './orders'
import Reservations from './reservations'
import { useAllReservationsList } from '@/_service/reservation'

const PAGE_LIMIT = 10

function OrderDesktop() {
  const { user } = useSelector((state: IRedux) => state.auth)
  const t = useTranslations()

  const [id, setId] = useQueryState('id')
  const [page, setPage] = useQueryState('page')
  const [selectedShipper] = useQueryState('shipper')
  const [orderType, setOrderType] = useQueryState('order_type', {
    defaultValue: 'delivery',
  })

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | string[]> = {
      page: page || 1,
      limit: PAGE_LIMIT,
      shipper_id: resolveBrandId(selectedShipper) || '',
    }

    // Add order_type filter
    if (orderType && orderType !== 'all') {
      params.order_type_ids = ORDER_TYPE_IDS[orderType]
    }

    return params
  }, [page, orderType, selectedShipper])

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['customer-orders', queryParams],
    queryFn: () => orderServiceMika.getList(user?.id || '', queryParams),
    enabled: Boolean(user && orderType !== 'hall'),
    refetchInterval: 10000,
  })

  const { data: reservationsData, isLoading: isReservationsLoading } =
    useAllReservationsList({
      params: queryParams,
      props: {
        refetchInterval: 10000,
      },
    })

  const handleDeliveryTypeChange = (value: string) => {
    setId('')
    setPage('')
    setOrderType(value)
  }

  // select first order after change order type
  useEffect(() => {
    if (ordersData?.orders?.length) {
      if (!id && !page) {
        setId(ordersData?.orders?.[0]?.id || '')
      }
    }
  }, [ordersData, id, page])

  return (
    <Box bg='background'>
      <Header />

      <Container maxW='1280px' px='10' h='full' mb={10}>
        <Flex justifyContent='space-between' direction='column' mb='6'>
          <Breadcrumbs
            data={[{ title: t('home'), link: '/' }, { title: t('my_orders') }]}
          />
          <Text fontWeight='bold' fontSize={24} color='text'>
            {t('my_orders')}
          </Text>
        </Flex>

        {/* Order Type Tabs */}
        <HStack alignItems={'center'} justifyContent={'space-between'} mb='4'>
          <DeliveryTypes handleDeliveryTypeChange={handleDeliveryTypeChange} />

          <BrandFilter />
        </HStack>

        {isLoading ? (
          <LoaderSkeleton />
        ) : orderType === 'hall' ? (
          <Reservations
            isLoading={isReservationsLoading}
            data={reservationsData}
          />
        ) : (
          <OrderList isLoading={isLoading} ordersData={ordersData} />
        )}
      </Container>
    </Box>
  )
}

export default OrderDesktop
